import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  X,
  UserPlus,
  ChevronDown,
  Check,
  Phone,
  Mail,
  Users,
  UsersRound,
  Briefcase,
  BadgeCheck,
  ArrowUpRight,
  Lock,
  Unlock,
  User,
  SlidersHorizontal,
} from 'lucide-react';
import InviteCustomerModal from './invite-modal';
import { useToast } from '../../Utils/toastContext';

/* ------------------------------------------------------------------ /
/ Customer categories — Individuals / Joint accounts / Sole traders /
/ ------------------------------------------------------------------ */
const CATEGORIES = {
  individual: {
    label: 'Individual',
    plural: 'Individuals',
    icon: User,
    color: '#375AE7',
    tint: 'rgba(55,90,231,0.10)',
    chip: 'bg-brand/10 text-brand ring-brand/20',
    desc: 'Single-holder customers',
  },
  joint: {
    label: 'Joint account',
    plural: 'Joint accounts',
    icon: UsersRound,
    color: '#8B5CF6',
    tint: 'rgba(139,92,246,0.12)',
    chip: 'bg-violet-50 text-violet-600 ring-violet-200',
    desc: 'Two or more holders',
  },
  sole: {
    label: 'Sole trader',
    plural: 'Sole traders',
    icon: Briefcase,
    color: '#10B981',
    tint: 'rgba(16,185,129,0.12)',
    chip: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
    desc: 'Self-employed / ABN',
  },
};
const CATEGORY_ORDER = ['individual', 'joint', 'sole'];

/* Whether any filter is currently applied (drives the chips row) */
const hasActiveFilters = (f) =>
  f.tab !== 'All' || f.cat !== 'all' || f.query.trim() !== '';

/* Removable chip used in the active-filters row */
function FilterChip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1 rounded-lg border border-brand/30 bg-brand/[0.06] text-[11px] font-semibold text-brand">
      {label}
      <button
        onClick={onClear}
        title={`Remove filter: ${label}`}
        className="w-5 h-5 rounded flex items-center justify-center hover:bg-brand/15 transition-colors"
      >
        <X size={11} strokeWidth={2.4} />
      </button>
    </span>
  );
}

/* ================================================================== /
/ API → UI MAPPING /
/ /
/ GET /api/v1/agency/customers /
/ ?page=1&limit=10[&status=..][&search=..] /
/   [&account_type_id=1&account_type_id=2]  (repeat param = OR) /
/ → { status, data: { customers: [], page, limit, total, /
/ total_pages, account_type_counts } } /
/ /
/ API field → UI field /
/ ---------------------------------------------------------------- /
/ user_id → id / user_id (row key + nav + block/unblock URL) /
/ full_name → name (falls back to first+last) /
/ first_name + last_name → initial (avatar initials) /
/ email_address → email /
/ country_code+mobile_number → mobile /
/ account_type.name → type (the ONE "Type" badge) /
/ account_type_id → category → badge colour + avatar + row route /
/ status.label → statusText + badge colour /
/ relationship_status → awaiting (sub-line under the badge) /
/ created_at → start / last (relative timestamps) /
/ client_id, unique_id → kept on the row /
/ case_progress.percent + label → Case progress column (flag ON) /
/ certificate.status → Certificate chip (flag ON) /
/ block_reason → reason shown under a Blocked badge /
/ data.total / total_pages → pagination footer + page numbers /
/ data.account_type_counts → hero line + category cards /
/   keyed by account_type_id: "1" Individual, "2"–"4" Joint, /
/   "5" Sole trader. A missing id counts as 0 — these are /
/   result-set totals, not the current page of rows. /
/ /
/ When case_progress / certificate are absent (flag OFF) they are /
/ derived from status.label (see TRACKING_BY_STATUS). /
/ /
/ Block / Unblock: /
/ PUT /api/v1/agency/customers/{user_id}/block    body { "reason" } /
/ PUT /api/v1/agency/customers/{user_id}/unblock /
/ → data: user_id, status, block_reason · already blocked → 400 · /
/ share-only customer → 403 /
/ ================================================================== */

/* account_type_id → category bucket — real API account types:
   1 = Individual
   2 = Spouse Joint Account
   3 = Joint Account (Same Address)
   4 = Joint Account (Different Address)
   5 = Sole Trader */
const CATEGORY_BY_ACCOUNT_TYPE_ID = {
  1: 'individual',
  2: 'joint',
  3: 'joint',
  4: 'joint',
  5: 'sole',
};

/* Category card → the account_type ids sent to the API as a filter.
The endpoint takes `account_type_id` as a REPEATED query param (OR),
e.g. Joint accounts sends
?account_type_id=2&account_type_id=3&account_type_id=4 */
const ACCOUNT_TYPE_PARAM = 'account_type_id';
const ACCOUNT_TYPE_IDS_BY_CATEGORY = {
  individual: [1],
  joint: [2, 3, 4],
  sole: [5],
};

/* Keyword fallback when the id isn't in the map above */
const categoryForAccountType = (id, name) => {
  if (CATEGORY_BY_ACCOUNT_TYPE_ID[id]) return CATEGORY_BY_ACCOUNT_TYPE_ID[id];
  const n = (name || '').toLowerCase();
  if (n.includes('joint')) return 'joint';
  if (n.includes('sole') || n.includes('trader') || n.includes('abn'))
    return 'sole';
  if (
    n.includes('trust') ||
    n.includes('company') ||
    n.includes('corporate') ||
    n.includes('business') ||
    n.includes('partnership')
  )
    return 'joint';
  return 'individual';
};

/* status.label → readable text in the Status column */
const STATUS_TEXT = {
  active: 'Active',
  onboarding: 'Onboarding',
  pending: 'Pending',
  to_review: 'In review',
  in_progress: 'In review',
  resubmit: 'In review',
  sent_for_resubmit: 'In review',
  sent_for_approval: 'In review',
  suspended: 'Suspended',
  archived: 'Archived',
  rejected: 'Blocked',
  blocked: 'Blocked',
};

const humanize = (s = '') =>
  String(s)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();

/* "SpouseJointAccount" → "Spouse Joint Account" (display only) */
const splitCamel = (s = '') =>
  String(s)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

/* "2026-09-14T23:05:23.810564Z" → "2h ago" */
const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
};

/* "2026-09-14" → "14 Sep 2026" */
const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/* Case-progress + certificate columns: when the flag is ON the API
sends case_progress { completed_phases, total_phases, percent, label }
and certificate { status }. Use those real values; when they are absent
derive them from status.label so the columns stay populated. */
const TRACKING_BY_STATUS = {
  active: { progress: 100, cert: 'Completed' },
  onboarding: { progress: 30, cert: 'Not started' },
  pending: { progress: 20, cert: 'Pending' },
  to_review: { progress: 70, cert: 'In progress' },
  in_progress: { progress: 70, cert: 'In progress' },
  resubmit: { progress: 55, cert: 'In progress' },
  sent_for_resubmit: { progress: 55, cert: 'In progress' },
  sent_for_approval: { progress: 85, cert: 'Pending' },
  suspended: { progress: 100, cert: 'Expired' },
  archived: { progress: 100, cert: 'Expired' },
  rejected: { progress: 0, cert: 'Not started' },
  blocked: { progress: 0, cert: 'Not started' },
};
const EMPTY_TRACKING = { progress: 0, cert: 'Not started' };

/* certificate.status (API) → chip label that matches CERT_BADGE keys */
const CERT_STATUS_TEXT = {
  not_started: 'Not started',
  in_progress: 'In progress',
  started: 'In progress',
  pending: 'Pending',
  awaiting: 'Pending',
  completed: 'Completed',
  complete: 'Completed',
  verified: 'Completed',
  issued: 'Completed',
  expired: 'Expired',
  revoked: 'Expired',
};
const mapCertStatus = (s) => {
  const k = String(s || '')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return CERT_STATUS_TEXT[k] || (s ? humanize(s) : '');
};

/* One API record → one table row */
const mapCustomer = (raw, index = 0) => {
  const r = raw || {};
  const firstName = r.first_name || '';
  const lastName = r.last_name || '';
  const fullName =
    r.full_name || `${firstName} ${lastName}`.trim() || 'Unnamed customer';
  const label = r.status?.label || 'pending';
  const accountId =
    r.account_type_id ?? r.account_type?.id ?? r.account_type?.value;
  /* Prefer the human-readable label; fall back to name and split
camelCase ("SpouseJointAccount" → "Spouse Joint Account"). */
  const rawTypeName = r.account_type?.label || r.account_type?.name || '';
  const accountName = rawTypeName ? splitCamel(rawTypeName) : '—';
  const category = categoryForAccountType(accountId, accountName);

  const initials =
    ((firstName[0] || '') + (lastName[0] || '')).toUpperCase() ||
    fullName
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .split(/\s+/)
      .map((w) => w[0] || '')
      .join('')
      .slice(0, 2)
      .toUpperCase() ||
    '??';

  return {
    id: r.user_id ?? index,
    user_id: r.user_id,
    name: fullName,
    email: r.email_address || '',
    mobile: [r.country_code, r.mobile_number].filter(Boolean).join(' '),
    /* "Type" column — account_type.name from the API */
    type: accountName,
    category,
    account_type_id: accountId,
    client_id: r.client_id || '',
    unique_id: r.unique_id || '',
    relationship_id: r.relationship_id,
    relationship_status: r.relationship_status || '',
    relationship_type: r.relationship_type || '',
    assigned_membership_id: r.assigned_membership_id,
    access_level: r.access_level,
    address: r.address,
    block_reason: r.block_reason || '',
    status: { id: r.status?.id, label },
    statusText: STATUS_TEXT[label] || humanize(label),
    /* sub-line under the status badge */
    awaiting: r.relationship_status
      ? `${humanize(r.relationship_status)} · ${relTime(r.created_at)}`
      : undefined,
    start: shortDate(r.created_at),
    last: relTime(r.created_at),
    initial: initials,
    created_at: r.created_at,
    /* Real progress / certificate when the flag is ON, else null → the
component derives them from status.label. */
    case_progress:
      r.case_progress && typeof r.case_progress.percent === 'number'
        ? r.case_progress
        : null,
    certText: mapCertStatus(r.certificate?.status),
    raw: r,
  };
};

/* Pull the customer array out of whatever envelope the API uses */
const extractCustomers = (response) => {
  const candidates = [
    response?.data?.customers,
    response?.customers,
    response?.data,
    response,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

/* Read total / total_pages from the response envelope:
{ data: { customers, page, limit, total, total_pages } } */
const numFrom = (response, keys) => {
  const scopes = [
    response?.data,
    response?.data?.meta,
    response?.data?.meta?.pagination,
    response?.data?.pagination,
    response?.meta,
    response?.pagination,
    response,
  ];
  for (const scope of scopes) {
    if (!scope || typeof scope !== 'object') continue;
    for (const k of keys) {
      if (typeof scope[k] === 'number') return scope[k];
    }
  }
  return undefined;
};

/* data.account_type_counts → { "1": 11, "5": 1 }
   Keys are account_type_id. Normalise to string keys + numbers so
   "1" and 1 are the same bucket. An empty / non-numeric map is
   treated as "field absent" so we can fall back to page counts. */
const normalizeAccountTypeCounts = (counts) => {
  if (!counts || typeof counts !== 'object' || Array.isArray(counts))
    return null;
  const out = {};
  let any = false;
  for (const [k, raw] of Object.entries(counts)) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) continue;
    out[String(k)] = n;
    any = true;
  }
  return any ? out : null;
};

const extractAccountTypeCounts = (response) => {
  const scopes = [
    response?.data?.account_type_counts,
    response?.account_type_counts,
    response?.data?.meta?.account_type_counts,
  ];
  for (const c of scopes) {
    const normalized = normalizeAccountTypeCounts(c);
    if (normalized) return normalized;
  }
  return null;
};

const accountTypeCount = (counts, id) => {
  if (!counts) return 0;
  const n = counts[String(id)];
  return typeof n === 'number' ? n : 0;
};

const sumAccountTypeIds = (counts, ids) =>
  (ids || []).reduce((sum, id) => sum + accountTypeCount(counts, id), 0);

const sumAllAccountTypeCounts = (counts) =>
  counts
    ? Object.values(counts).reduce(
        (sum, n) => sum + (typeof n === 'number' ? n : 0),
        0,
      )
    : 0;

/* A type-filtered list can come back with only the selected ids.
   Keep the last full breakdown for the same status|search scope so
   the other cards don't drop to 0. A new status/search, or a map
   that still includes other types, replaces the breakdown outright
   (missing ids then correctly count as 0). */
const mergeAccountTypeCounts = (prev, next, cat, sameScope) => {
  const incoming = normalizeAccountTypeCounts(next);
  if (!incoming) return cat === 'all' || !sameScope ? null : prev;
  if (cat === 'all' || !sameScope || !prev) return incoming;
  const selected = (ACCOUNT_TYPE_IDS_BY_CATEGORY[cat] || []).map(String);
  const selectedSet = new Set(selected);
  const keys = Object.keys(incoming);
  const narrowed = keys.length > 0 && keys.every((k) => selectedSet.has(k));
  if (!narrowed) return incoming;
  const merged = { ...prev };
  selected.forEach((id) => {
    if (Object.prototype.hasOwnProperty.call(incoming, id)) {
      merged[id] = incoming[id];
    }
  });
  return merged;
};

const STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
  onboarding: 'bg-sky-50 text-sky-600 ring-sky-200',
  pending: 'bg-amber-50 text-amber-600 ring-amber-200',
  review: 'bg-violet-50 text-violet-600 ring-violet-200',
  suspended: 'bg-slate-100 text-slate-500 ring-slate-200',
  blocked: 'bg-red-50 text-red-600 ring-red-200',
};

/* Map a real status.label onto this UI's badge style */
const BADGE_FOR_LABEL = {
  active: 'active',
  onboarding: 'onboarding',
  pending: 'pending',
  to_review: 'review',
  in_progress: 'review',
  resubmit: 'review',
  sent_for_resubmit: 'review',
  sent_for_approval: 'review',
  suspended: 'suspended',
  archived: 'suspended',
  rejected: 'blocked',
  blocked: 'blocked',
};
const badgeFor = (p) => BADGE_FOR_LABEL[p.status?.label] ?? 'active';

/* The block endpoint sets status "blocked"; the list may also report
the legacy "rejected" — both render as Blocked. */
const isBlocked = (p) => ['rejected', 'blocked'].includes(p.status?.label);

/* Status filter tabs */
const TABS = ['All', 'Active', 'Onboarding', 'Pending', 'In review', 'Blocked'];

/* ================================================================== /
/ Main Customers page /
/ ================================================================== */
const LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

/* ------------------------------------------------------------------ /
/ Table grid — the header and every row share this ONE definition, so
the columns can never drift apart.


14 tracks, allocated 1 + 3 + 2 + 2 + 2 + 2 + 2:
  avatar · Customer · Type · Status · Case progress · Certificate · Action

`minmax(0,1fr)` matters: a grid item's default minimum width is its
content width, so without it a long email forces the track wider than
its span and every following column slides out of alignment (and
`truncate` silently does nothing). min-w-0 on the cell + truncate on
the text is what keeps the row on one line.                            */
const GRID_COLS = 'md:grid-cols-[repeat(14,minmax(0,1fr))]';

/* Tab → the status query value (All sends nothing) */
const FILTER_BY_TAB = {
  All: 'all',
  Active: 'active',
  Onboarding: 'onboarding',
  Pending: 'pending',
  'In review': 'to_review',
  Blocked: 'rejected',
};

/* Page-number strip: 1 … 4 5 6 … 20 (same button styles as before) */
const pageWindow = (count, current) => {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const keep = new Set([1, count, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => keep.add(n));
  if (current >= count - 2)
    [count - 3, count - 2, count - 1].forEach((n) => keep.add(n));
  const sorted = [...keep]
    .filter((n) => n >= 1 && n <= count)
    .sort((a, b) => a - b);
  const out = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push('…');
    out.push(n);
  });
  return out;
};

/* ------------------------------------------------------------------ /
/ Redux wiring — in your real app, replace these stubs with:
import { useDispatch } from 'react-redux';
import {
clearCustomerName,
setOnboardingCustomer,
} from '<your store>/actions';
const dispatch = useDispatch(); // inside CustomerList
------------------------------------------------------------------ */
const clearCustomerName = () => ({ type: 'CLEAR_CUSTOMER_NAME' });
const setOnboardingCustomer = (user_id) => ({
  type: 'SET_ONBOARDING_CUSTOMER',
  payload: { user_id },
});
const dispatch = (action) => action; // TODO: replace with the real dispatch

export default function CustomerList() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const component = location.state?.component;
  /* When opened from the dashboard, land directly on the In review tab */
  const [tab, setTab] = useState(
    component === 'dashboard' ? 'In review' : 'All',
  );

  useEffect(() => {
    if (component === 'dashboard') {
      setTab('In review');
    }
  }, [component]);

  const [cat, setCat] = useState('all');
  const [query, setQuery] = useState('');
  /* Search is debounced before it hits the API as search */
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  /* Current page of rows + the pagination the API reported */
  const [people, setPeople] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  /* data.account_type_counts — result-set totals, not this page. */
  const [accountTypeCounts, setAccountTypeCounts] = useState(null);

  /* Block/unblock state: which customer's row is in flight, plus the
reason dialog (the block API requires a reason in the body). */
  const [blockingId, setBlockingId] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  /* Guards against out-of-order responses (fast typing / tab switching) */
  const requestSeq = useRef(0);
  /* status|search the stored account_type_counts belong to. */
  const countsScopeRef = useRef('');

  /* showToast is read through a ref so the fetch effect doesn't depend
on it — if the toast context isn't memoised, a new function identity
every render would retrigger the request in a loop. */
  const toastRef = useRef(showToast);
  useEffect(() => {
    toastRef.current = showToast;
  }, [showToast]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const CERT_BADGE = {
    Completed: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
    'In progress': 'bg-brand/10 text-brand ring-brand/20',
    'Not started': 'bg-slate-100 text-slate-500 ring-slate-200',
    Pending: 'bg-amber-50 text-amber-600 ring-amber-200',
    Expired: 'bg-violet-50 text-violet-600 ring-violet-200',
  };

  /* Per-person case tracking. Prefers the real API fields
(case_progress + certificate, present when the flag is ON) and falls
back to values derived from status.label when they're absent. */
  const trk = (p) => {
    const cp = p.case_progress;
    const hasCp = !!cp && typeof cp.percent === 'number';
    const derived = TRACKING_BY_STATUS[p.status?.label] ?? EMPTY_TRACKING;

    const progress = hasCp
      ? Math.max(0, Math.min(100, Math.round(cp.percent)))
      : derived.progress;

    const cert = p.certText || derived.cert;

    const phasesText = hasCp
      ? cp.label ||
        (cp.total_phases
          ? `${cp.completed_phases} of ${cp.total_phases} phases`
          : progress === 100
            ? 'Fully completed'
            : 'In progress')
      : progress === 100
        ? 'Fully completed'
        : progress > 0
          ? `${Math.ceil(progress / 20)} of 5 phases`
          : 'Not started';

    return { progress, cert, phasesText };
  };

  /* filter as the real app sees it — derived from the active tab here */
  const filter = FILTER_BY_TAB[tab] ?? 'all';

  /* ---------------- Record click → flow navigation ----------------
Every row is clickable, including onboarding / pending records.
suspended · archived · active · rejected · blocked → /customer-details/{id}
everything else (onboarding, pending, to_review, in_progress,
resubmit, sent_for_resubmit, sent_for_approval, …) routes by
account_type_id:
1 (Individual) · 5 (Sole Trader) → /review-kyc/{id}
2–4 (Joint accounts) → /review-kyc-corporate/{id}
unknown ids → /review-kyc-trust/{id} */
  const handleRecordClick = (customer) => {
    const navState = {
      from: location.pathname,
      historyStack: [
        ...(location.state?.historyStack || []),
        location.pathname,
      ],
    };

    if (
      ['suspended', 'archived', 'active', 'rejected', 'blocked'].includes(
        customer.status.label,
      ) ||
      filter == 'suspended' ||
      filter == 'archived'
    ) {
      dispatch(clearCustomerName());
      let redirectState = {
        path: `/customer-details/${customer.user_id}`,
        state: navState,
      };
      sessionStorage.setItem(
        'redirectAfterLogin',
        JSON.stringify(redirectState),
      );
      navigate(redirectState.path, { state: redirectState.state });
      return;
    }

    /* onboarding / pending / any in-flight KYC status */
    dispatch(setOnboardingCustomer(customer.user_id));
    const accountType = customer.account_type_id;

    let path = '';

    /* 1 = Individual, 5 = Sole Trader → individual KYC flow
   2–4 = Joint accounts → multi-holder (corporate) flow */
    if ([1, 2, 3, 4, 5].includes(accountType)) {
      path = `/review-kyc/${customer.user_id}`;
    } else if ([6, 7, 8].includes(accountType)) {
      path = `/review-kyc-corporate/${customer.user_id}`;
    } else {
      path = `/review-kyc-trust/${customer.user_id}`;
    }

    let redirectState = {
      path,
      state: navState,
    };

    // Save to session
    sessionStorage.setItem('redirectAfterLogin', JSON.stringify(redirectState));

    // Navigate using the SAME computed path
    navigate(path, { state: redirectState.state });
  };

  /* Single fetch wrapper: auth header, 401 → login, error envelope.
Used by GET (list) and PUT (block/unblock) calls alike. */
  const doFetch = useCallback(
    async (path, { method = 'GET', body, isBlob = false } = {}) => {
      const token = localStorage.getItem('jwtToken');
      const apiUrl = `${BASE_URL}${path}`;

      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (body) headers['Content-Type'] = 'application/json';

      const response = await fetch(apiUrl, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired.');
        }

        const errorData = await response.json().catch(() => ({}));
        const msg =
          (typeof errorData === 'string' ? errorData : errorData.message) ||
          errorData.detail ||
          `Request failed (${response.status})`;
        throw new Error(typeof msg === 'string' ? msg : 'Unexpected error');
      }

      return isBlob ? response.blob() : response.json();
    },
    [BASE_URL, navigate],
  );

  const GetAPIcalls = useCallback(
    (url, isBlob = false) => doFetch(url, { isBlob }),
    [doFetch],
  );

  /* Build ?page=&limit=[&status=][&search=]
   [&account_type_id=1&account_type_id=2] (repeat param = OR) */
  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(LIMIT));

    const status = FILTER_BY_TAB[tab];
    if (status && status !== 'all') params.set('status', status);

    if (search) params.set('search', search);

    /* Account type filter: the endpoint expects account_type_id as a
REPEATED query param (OR across the ids), e.g.
?account_type_id=6&account_type_id=7&account_type_id=8 */
    if (cat !== 'all') {
      const ids = ACCOUNT_TYPE_IDS_BY_CATEGORY[cat] || [];
      ids.forEach((id) => params.append(ACCOUNT_TYPE_PARAM, String(id)));
    }
    return params.toString();
  }, [page, tab, search, cat]);

  const getcustomers = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/customers?${buildQuery()}`,
      );

      if (response?.status == 401) {
        localStorage.removeItem('jwtToken');
        navigate('/login');
        return;
      }

      const rows = extractCustomers(response).map(mapCustomer);
      const apiTotal = numFrom(response, [
        'total',
        'total_count',
        'totalCount',
      ]);
      const apiPages = numFrom(response, [
        'total_pages',
        'totalPages',
        'last_page',
      ]);
      const apiTypeCounts = extractAccountTypeCounts(response);
      const scope = `${FILTER_BY_TAB[tab] ?? 'all'}|${search}`;

      if (seq !== requestSeq.current) return; // a newer request is in flight

      /* Fall back to an inferred total when the API omits it */
      const nextTotal =
        typeof apiTotal === 'number'
          ? apiTotal
          : (page - 1) * LIMIT + rows.length;
      const nextPages =
        typeof apiPages === 'number'
          ? apiPages
          : typeof apiTotal === 'number'
            ? Math.max(1, Math.ceil(apiTotal / LIMIT))
            : rows.length < LIMIT
              ? page
              : page + 1;

      setPeople(rows);
      setTotal(nextTotal);
      setTotalPages(Math.max(1, nextPages));
      const sameScope = countsScopeRef.current === scope;
      countsScopeRef.current = scope;
      setAccountTypeCounts((prev) =>
        mergeAccountTypeCounts(prev, apiTypeCounts, cat, sameScope),
      );
    } catch (err) {
      if (seq !== requestSeq.current) return;
      toastRef.current(err.message, 'error');
      setPeople([]);
      setTotal(0);
      setTotalPages(1);
      countsScopeRef.current = '';
      setAccountTypeCounts(null);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [GetAPIcalls, buildQuery, navigate, page, cat, tab, search]);

  /* Any change to page / status / search / category refetches */
  useEffect(() => {
    getcustomers();
  }, [getcustomers]);

  /* If a refetch shrinks the result set below the page we're on (deleted
row, narrower filter), step back onto the last real page. */
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageCount = Math.max(1, totalPages);
  const safePage = Math.min(page, pageCount);
  const rangeStart = people.length ? (safePage - 1) * LIMIT + 1 : 0;
  const rangeEnd = (safePage - 1) * LIMIT + people.length;

  /* Category cards + hero. Prefer data.account_type_counts so the
     numbers cover every page, not just the 10 rows on screen.
     1 → Individual, 2+3+4 → Joint, 5 → Sole trader. Missing id = 0.
     Fall back to the current page only if the API omits the field. */
  const pageCounts = {
    all: cat === 'all' ? total : people.length,
    individual:
      cat === 'individual'
        ? total
        : people.filter((p) => p.category === 'individual').length,
    joint:
      cat === 'joint'
        ? total
        : people.filter((p) => p.category === 'joint').length,
    sole:
      cat === 'sole'
        ? total
        : people.filter((p) => p.category === 'sole').length,
  };
  const catCounts = accountTypeCounts
    ? {
        individual: sumAccountTypeIds(
          accountTypeCounts,
          ACCOUNT_TYPE_IDS_BY_CATEGORY.individual,
        ),
        joint: sumAccountTypeIds(
          accountTypeCounts,
          ACCOUNT_TYPE_IDS_BY_CATEGORY.joint,
        ),
        sole: sumAccountTypeIds(
          accountTypeCounts,
          ACCOUNT_TYPE_IDS_BY_CATEGORY.sole,
        ),
        /* Unfiltered list total matches the pagination footer. When a
           type card is selected, `total` is only that type — keep All
           as the sum of the facet map instead. */
        all: cat === 'all' ? total : sumAllAccountTypeCounts(accountTypeCounts),
      }
    : pageCounts;

  const currentFilters = { tab, cat, query };

  const gotoPage = (p) => setPage(Math.max(1, Math.min(p, pageCount)));

  const addCustomer = (form) => {
    /* Build an API-shaped record and run it through the SAME mapper so
locally-added rows look identical to fetched ones. */
    const raw = {
      user_id: Date.now(),
      first_name: form.firstName,
      last_name: form.lastName,
      full_name: `${form.firstName} ${form.lastName}`.trim(),
      email_address: form.email,
      country_code: form.cc,
      mobile_number: form.mobile,
      status: { id: 6, label: 'onboarding' },
      client_id: '',
      account_type_id: 1,
      account_type: { id: 1, name: form.appType || 'Individual' },
      created_at: new Date().toISOString(),
      relationship_status: 'invited',
      relationship_type: 'direct',
    };

    setPeople((p) => [mapCustomer(raw), ...p]);
    setTotal((t) => t + 1);
    /* Invite creates an Individual (account_type_id 1). Bump that
       facet so the cards match until the list refetch lands. */
    setAccountTypeCounts((prev) => {
      if (!prev) return prev;
      return { ...prev, 1: accountTypeCount(prev, 1) + 1 };
    });
    setToast(`Invite sent to “${raw.full_name}”`);
    setTimeout(() => setToast(null), 2600);
  };

  /* ---------------- Block / Unblock (PUT APIs) ----------------
PUT /api/v1/agency/customers/{user_id}/block   body { "reason" }
PUT /api/v1/agency/customers/{user_id}/unblock
Response data: user_id, status, block_reason
Already blocked → 400 · share-only customer → 403 */
  const openBlockModal = (customer) => {
    setBlockReason('');
    setBlockTarget(customer);
  };

  const closeBlockModal = () => {
    if (blockingId == null) setBlockTarget(null);
  };

  const blockCustomer = useCallback(
    async (customer, reason) => {
      const id = customer.user_id;
      if (id == null) {
        toastRef.current('Missing customer id.', 'error');
        return;
      }
      setBlockingId(id);
      try {
        const res = await doFetch(`/api/v1/agency/customers/${id}/block`, {
          method: 'PUT',
          body: { reason: (reason || '').trim() || 'Blocked by agency' },
        });
        /* Server truth: { data: { user_id, status: "blocked", block_reason } } */
        const d = res?.data ?? res ?? {};
        const label = d.status && STATUS_TEXT[d.status] ? d.status : 'blocked';
        setPeople((prev) =>
          prev.map((p) =>
            p.user_id === id
              ? {
                  ...p,
                  status: { ...(p.status || {}), label },
                  statusText: STATUS_TEXT[label] || humanize(label),
                  block_reason: d.block_reason || reason || '',
                }
              : p,
          ),
        );
        setBlockTarget(null);
        toastRef.current(`Blocked ${customer.name}`, 'success');
      } catch (err) {
        toastRef.current(err.message || 'Could not block customer.', 'error');
      } finally {
        setBlockingId(null);
      }
    },
    [doFetch],
  );

  const unblockCustomer = useCallback(
    async (customer) => {
      const id = customer.user_id;
      if (id == null) {
        toastRef.current('Missing customer id.', 'error');
        return;
      }
      setBlockingId(id);
      try {
        const res = await doFetch(`/api/v1/agency/customers/${id}/unblock`, {
          method: 'PUT',
        });
        const d = res?.data ?? res ?? {};
        const label = d.status && STATUS_TEXT[d.status] ? d.status : 'active';
        setPeople((prev) =>
          prev.map((p) =>
            p.user_id === id
              ? {
                  ...p,
                  status: { ...(p.status || {}), label },
                  statusText: STATUS_TEXT[label] || humanize(label),
                  block_reason: '',
                }
              : p,
          ),
        );
        toastRef.current(`Unblocked ${customer.name}`, 'success');
      } catch (err) {
        toastRef.current(err.message || 'Could not unblock customer.', 'error');
      } finally {
        setBlockingId(null);
      }
    },
    [doFetch],
  );

  const catFor = (p) => CATEGORIES[p.category] ?? CATEGORIES.individual;

  return (
    <div className="min-h-screen bg-white rounded-2xl text-slate-900 font-sans lg:min-w-[78vw] lg:max-w-[80vw]">
      <main className="px-4 md:px-6 lg:px-8 pt-6 md:pt-8 pb-14 mx-auto">
        {/* ---------- Hero header band ---------- */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1B2668] via-[#375AE7] to-[#5B7CFA] shadow-lift">
          <div className="absolute -top-24 right-10 w-80 h-80 rounded-full bg-white/10 blur-3xl animate-drift pointer-events-none" />
          <div className="absolute -bottom-28 -left-16 w-80 h-80 rounded-full bg-[#22D3EE]/20 blur-3xl animate-drift-slow pointer-events-none" />

          <div className="relative p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1 min-w-0">
              <h1 className="mt-1.5 text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-2.5">
                Customers
                <span className="w-9 h-9 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center">
                  <Users size={18} strokeWidth={1.9} className="text-white" />
                </span>
              </h1>
              <p className="mt-1.5 text-sm text-white/75">
                {catCounts.all} customers · {catCounts.individual} individuals ·{' '}
                {catCounts.joint} joint accounts · {catCounts.sole} sole traders
              </p>
            </div>

            <div className="flex items-center gap-2.5 shrink-0">
              <button
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white text-brand text-sm font-bold shadow-md hover:bg-white/90 transition-all active:scale-[0.97]"
              >
                <Plus size={16} />
                Invite customer
              </button>
            </div>
          </div>
        </div>

        {/* ---------- Category summary cards (clickable filters) ---------- */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {/* All */}
          <button
            onClick={() => {
              setPage(1);
              setCat('all');
            }}
            className={`relative overflow-hidden text-left rounded-xl border p-4 transition-all duration-200 hover:-translate-y-0.5 ${
              cat === 'all'
                ? 'border-brand bg-brand/[0.04] ring-2 ring-brand/30 shadow-card'
                : 'border-slate-200 bg-white shadow-sm hover:shadow-card'
            }`}
          >
            <div className="flex items-center gap-3">
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background:
                    cat === 'all' ? 'rgba(55,90,231,0.10)' : '#F1F5F9',
                  color: cat === 'all' ? '#375AE7' : '#64748B',
                }}
              >
                <Users size={18} strokeWidth={1.9} />
              </span>
              <div>
                <p className="text-xl font-bold text-slate-900 tabular-nums leading-6">
                  {catCounts.all}
                </p>
                <p className="text-[11px] text-slate-500">All customers</p>
              </div>
            </div>
          </button>

          {CATEGORY_ORDER.map((key) => {
            const c = CATEGORIES[key];
            const Icon = c.icon;
            const active = cat === key;
            return (
              <button
                key={key}
                onClick={() => {
                  setPage(1);
                  setCat(key);
                }}
                className={`relative overflow-hidden text-left rounded-xl border p-4 transition-all duration-200 hover:-translate-y-0.5 ${
                  active
                    ? 'border-transparent shadow-card'
                    : 'border-slate-200 bg-white shadow-sm hover:shadow-card'
                }`}
                style={
                  active
                    ? {
                        boxShadow: `0 18px 34px -22px ${c.color}99`,
                        background: c.tint,
                      }
                    : undefined
                }
              >
                <span
                  className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full"
                  style={{ background: c.color }}
                />
                <div className="flex items-center gap-3">
                  <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: c.tint, color: c.color }}
                  >
                    <Icon size={18} strokeWidth={1.9} />
                  </span>
                  <div>
                    <p
                      className="text-xl font-bold tabular-nums leading-6"
                      style={{ color: c.color }}
                    >
                      {catCounts[key]}
                    </p>
                    <p className="text-[11px] text-slate-600">{c.plural}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* ---------- Filter row ---------- */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 min-w-0">
          {/* Pill tabs */}
          <div className="flex sm:inline-flex items-center gap-1 rounded-full bg-white p-1 ring-1 ring-slate-200 shadow-sm w-full max-w-full sm:w-fit overflow-x-auto overscroll-x-contain scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden -mx-1 px-2 sm:mx-0 sm:px-1">
            {TABS.map((t) => {
              const active = tab === t;
              return (
                <button
                  key={t}
                  onClick={() => {
                    setPage(1);
                    setTab(t);
                  }}
                  className={`relative inline-flex shrink-0 snap-start items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap ${
                    active
                      ? 'bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white shadow-md shadow-brand/25'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative">
            <Search
              size={15}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={query}
              onChange={(e) => {
                setPage(1);
                setQuery(e.target.value);
              }}
              placeholder="Search customers, email, type…"
              className="h-11 pl-10 pr-4 w-full sm:w-64 rounded-full border border-slate-200 bg-white text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition"
            />
          </div>
        </div>

        {/* ---------- Active filter chips (what's currently applied) ---------- */}
        {hasActiveFilters(currentFilters) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <SlidersHorizontal size={11} />
              Active filters
            </span>
            {tab !== 'All' && (
              <FilterChip
                label={`Status: ${tab}`}
                onClear={() => {
                  setTab('All');
                  setPage(1);
                }}
              />
            )}
            {cat !== 'all' && (
              <FilterChip
                label={`Type: ${CATEGORIES[cat].plural}`}
                onClear={() => {
                  setCat('all');
                  setPage(1);
                }}
              />
            )}
            {query.trim() !== '' && (
              <FilterChip
                label={`Search: “${query.trim()}”`}
                onClear={() => {
                  setQuery('');
                  setPage(1);
                }}
              />
            )}
            <button
              onClick={() => {
                setTab('All');
                setCat('all');
                setQuery('');
                setPage(1);
              }}
              className="h-7 px-2 rounded-lg text-[11px] font-semibold text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            >
              Clear all
            </button>
          </div>
        )}

        {/* ---------- Customers table (card rows) ---------- */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200/70 shadow-card overflow-hidden">
          {/* Column header */}
          <div
            className={`hidden md:grid ${GRID_COLS} gap-3 px-5 py-3 bg-gradient-to-r from-[#FAFBFF] to-[#F1F5FC] border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wide text-slate-400`}
          >
            <span className="md:col-span-1" />
            <span className="md:col-span-3">Customer</span>
            <span className="md:col-span-2">Type</span>
            <span className="md:col-span-2">Status</span>
            <span className="md:col-span-2">Case progress</span>
            <span className="md:col-span-2">Certificate</span>
            <span className="md:col-span-2 text-right">Action</span>
          </div>

          {/* Rows */}
          <div className="divide-y divide-slate-100">
            {people.map((p, idx) => {
              const c = catFor(p);
              const Icon = c.icon;
              return (
                <div
                  key={p.user_id ?? p.id ?? idx}
                  onClick={() => handleRecordClick(p)}
                  className={`group relative grid grid-cols-1 ${GRID_COLS} items-center gap-3 px-5 py-4 transition-all duration-200 hover:bg-[#F8FAFF] cursor-pointer`}
                  style={{
                    animation: `fade-up 480ms ${idx * 45}ms cubic-bezier(.22,1,.36,1) both`,
                  }}
                >
                  {/* category accent rail (always visible) */}
                  <span
                    className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full"
                    style={{ background: c.color }}
                  />

                  {/* Category avatar */}
                  <div className="md:col-span-1 min-w-0 md:text-center">
                    <span
                      className="relative inline-flex w-10 h-10 rounded-full items-center justify-center text-[11px] font-bold ring-2 ring-white transition-transform duration-200 group-hover:scale-110"
                      style={{ background: c.tint, color: c.color }}
                    >
                      {p.initial}
                    </span>
                  </div>

                  {/* Customer */}
                  <div className="md:col-span-3 min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <Icon
                        size={12}
                        className="shrink-0"
                        style={{ color: c.color }}
                      />
                      <p className="truncate text-sm font-semibold text-slate-800 transition-colors group-hover:text-brand group-hover:underline">
                        {p.name}
                      </p>
                    </div>
                    <p className="truncate text-[11px] text-slate-400">
                      {p.email}
                    </p>
                  </div>

                  {/* Type — ONE badge: account_type.name, tinted by
                  Individual / Joint account / Sole trader */}
                  <div className="md:col-span-2 min-w-0">
                    <span
                      className={`inline-flex max-w-full items-center gap-1 text-[10px] font-semibold rounded-md px-2 py-1 ring-1 ${c.chip}`}
                    >
                      <Icon size={10} className="shrink-0" />
                      <span className="truncate">{p.type}</span>
                    </span>
                  </div>

                  {/* Status */}
                  <div className="md:col-span-2 min-w-0">
                    <span
                      title={p.block_reason || undefined}
                      className={`inline-flex max-w-full items-center gap-1.5 text-[10px] font-bold rounded-full px-2 py-0.5 ring-1 ${STATUS_BADGE[badgeFor(p)] ?? STATUS_BADGE.active}`}
                    >
                      <span className="h-1.5 w-1.5 shrink-0 animate-pulse-dot rounded-full bg-current" />
                      <span className="truncate">{p.statusText}</span>
                    </span>
                    {p.awaiting && (
                      <p className="mt-1 truncate text-[10px] text-slate-400">
                        {p.awaiting}
                      </p>
                    )}
                    {isBlocked(p) && p.block_reason && (
                      <p className="mt-1 truncate text-[10px] text-slate-400">
                        Reason: {p.block_reason}
                      </p>
                    )}
                  </div>

                  {/* Case progress — real API value when the flag is ON
                  (case_progress.percent / label), else derived */}
                  <div className="md:col-span-2 min-w-0">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[10px] font-bold tabular-nums text-slate-600">
                        {trk(p).progress}%
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${trk(p).progress}%`,
                          background: `linear-gradient(90deg, ${c.color}, ${c.color}cc)`,
                          transitionDelay: `${idx * 60}ms`,
                        }}
                      />
                    </div>
                    <p className="mt-1 truncate text-[9px] text-slate-400">
                      {trk(p).phasesText}
                    </p>
                  </div>

                  {/* Certificate — certificate.status when the flag is ON,
                  else derived from the status */}
                  <div className="md:col-span-2 min-w-0">
                    <span
                      className={`inline-flex max-w-full items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 ring-1 ${CERT_BADGE[trk(p).cert] ?? CERT_BADGE['Not started']}`}
                    >
                      <BadgeCheck size={11} className="shrink-0" />
                      <span className="truncate">{trk(p).cert}</span>
                    </span>
                  </div>

                  {/* Action */}
                  <div className="md:col-span-2 flex min-w-0 items-center justify-end gap-1.5">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isBlocked(p)) unblockCustomer(p);
                        else openBlockModal(p);
                      }}
                      disabled={blockingId != null}
                      className={`inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[11px] font-semibold transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed ${
                        isBlocked(p)
                          ? 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                          : 'text-red-600 bg-white border-red-200 hover:bg-red-50'
                      }`}
                    >
                      {isBlocked(p) ? (
                        <>
                          <Unlock size={13} />{' '}
                          {blockingId === p.user_id ? 'Unblocking…' : 'Unblock'}
                        </>
                      ) : (
                        <>
                          <Lock size={13} />{' '}
                          {blockingId === p.user_id ? 'Blocking…' : 'Block'}
                        </>
                      )}
                    </button>
                    {/* FIXED: colour is now driven by the button's OWN hover
                    state only. Previously `group-hover:text-brand` was
                    active at the same time as `hover:text-white`, and
                    depending on the Tailwind version/CSS order the brand
                    blue text won — blue arrow on a blue background.
                    Removing the group-hover colour makes white-on-brand
                    deterministic in every Tailwind version. */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRecordClick(p);
                      }}
                      title="Open record"
                      className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400 opacity-0 ring-1 ring-slate-200 transition-all duration-200 hover:bg-brand hover:text-white hover:ring-brand/40 focus-visible:opacity-100 group-hover:opacity-100 md:inline-flex"
                    >
                      <ArrowUpRight size={14} />
                    </button>
                  </div>
                </div>
              );
            })}

            {people.length === 0 && (
              <div className="py-16 text-center">
                <Users size={30} className="mx-auto text-slate-300" />
                <p className="text-sm text-slate-500 mt-3">
                  {loading
                    ? 'Loading customers…'
                    : 'No customers match your filters'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ---------- Pagination (server-driven: page / limit) ---------- */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[11px] text-slate-400">
            Showing{' '}
            <b className="text-slate-600">
              {rangeStart}–{rangeEnd}
            </b>{' '}
            of <b className="text-slate-600">{total}</b> customers · {LIMIT} per
            page
          </p>

          {pageCount > 1 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => gotoPage(safePage - 1)}
                disabled={safePage === 1}
                className="h-9 px-3 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              {pageWindow(pageCount, safePage).map((p, i) =>
                p === '…' ? (
                  <span
                    key={`gap-${i}`}
                    className="h-9 w-9 inline-flex items-center justify-center text-xs font-bold text-slate-300 select-none"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => gotoPage(p)}
                    className={`h-9 w-9 rounded-lg text-xs font-bold transition-all duration-200 ${
                      safePage === p
                        ? 'bg-gradient-to-r from-[#375AE7] to-[#5B7CFA] text-white shadow-md shadow-brand/25'
                        : 'text-slate-600 border border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}
              <button
                onClick={() => gotoPage(safePage + 1)}
                disabled={safePage === pageCount}
                className="h-9 px-3 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>

      {/* ---------- Invite customer modal ---------- */}
      <InviteCustomerModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          getcustomers();
        }}
        onSave={addCustomer}
      />

      {/* ---------- Block reason modal (block API requires a reason) ---------- */}
      {blockTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={closeBlockModal}
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-lift">
            <h3 className="text-base font-bold text-slate-900">
              Block customer
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              {blockTarget.name}
              {blockTarget.email ? ` (${blockTarget.email})` : ''} will be
              blocked from accessing the platform.
            </p>
            <textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Reason (e.g. Policy violation)"
              rows={3}
              className="mt-3 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition resize-none"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={closeBlockModal}
                disabled={blockingId != null}
                className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => blockCustomer(blockTarget, blockReason)}
                disabled={blockingId != null}
                className="inline-flex h-10 items-center gap-2 px-4 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 active:scale-[0.97] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Lock size={14} />
                {blockingId === blockTarget.user_id
                  ? 'Blocking…'
                  : 'Block customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Toast ---------- */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold rounded-xl px-4 py-3 shadow-lift animate-toast-in">
          <Check size={16} className="text-emerald-400" />
          {toast}
        </div>
      )}
    </div>
  );
}
